// import { call, put, select, takeEvery, take, fork, spawn } from 'redux-saga/effects';
// import { createEventChannel } from '../sagas_utils/sagas_channels';
import { call, put, takeEvery, take, spawn } from 'redux-saga/effects';
import { browserHistory } from 'react-router';
import env from '../../config/environment';

import sagasAdmin from '../../sagas/sagasAdmin';
import { callFetchAllPreferenceSettings } from '../preferences/saga_preferences';
import * as SagasType from '../../constants/constant_saga_types';
import { storeUserLocale, getUserLocale } from '../../locale';
import * as apiCommon from '../../services/apiCommon';
import * as apiEmployee from '../../services/employee/api_employee';

import {
  getLocalStorage,
  setLocalStorage,
} from 'ks-client-admin/utils/local-storage';

import Client from '@krispsystems/http-client';

import * as fc from 'ks-feathers-client/constants';

import {
  FETCH_INVENTORY_STATUS,
  FETCH_TRANSACTION_STATUS,
  FETCH_ENTITY_TYPE,
  FETCH_EMPLOYEE_ROLE_AUTHORITY_LIST,
  FETCH_ADDRESS_TYPE_LIST,
  FETCH_COUNTRY_LIST,
} from '../../constants/constant_action_types';

import { callSetHeaderbarSubsidiary, callSetPrimarySubsidiary } from '../subsidiary/sagasSubsidiary';
import { authenticate, logout } from '../../services/auth/api_auth';
import { setCookieYear } from "../../utils";

const environment = env();

export function* initAuthenticationSaga() {
  try {
    yield take('INIT_AUTHENTICATION');
    yield call(callAuthentication);
  } catch (e) {
    console.error('Error encountered at initialization: ', e);
    console.error('Application failed to initialize. Refresh the page');
    throw e;
  }
}

export function* callAuthentication() {
  const { token, payload, isExpired } = yield call(authenticate);

  if (isExpired) {
    browserHistory.push({
      pathname: '/admin/logout',
      state: { message: 'Your token has expired, please login again' },
    });

    return;
  }

  if (environment === 'development' || environment === 'local_development') {
    setCookieYear('ks-app-token-admin', token);
  }

  yield put({ type: fc.SET_FEATHERS_PAYLOAD, payload });

  // Use this in [PRODUCTION]
  const { api_host } = payload;
  const apiHostUrl = `https://${api_host}`;

  // Use this in [DEVELOPMENT]
  // Change the api port - i.e 8083
  // const apiHostUrl = `http://localhost:8002`;

  const client = new Client({
    token,
    baseUrl: apiHostUrl,
  });

  yield put({ type: fc.SET_FEATHERS_API_CLIENT, payload: client });
  yield put({ type: fc.SET_API_HOST_URL, payload: apiHostUrl });
  yield put({ type: fc.SET_API_ACCESS_TOKEN, payload: token });

  /* Setup socket handlers */
  yield put({ type: 'SERVER_CONNECTED' });
  yield spawn(sagasAdmin, client);

  /* Check user access again */
  const user = yield call(getEmployeeDetails, client, payload.employee_id);

  // Logout and redirect to "Unauthorized Access" page if user is not active
  // or access is disabled
  if (!user || !user.employee_access_enabled || !user.active) {
    yield call(logout, { redirect: false });
    browserHistory.push('/admin/unauthorized?code=ENV_NO_ACCESS');
    return;
  }

  /* Set locale of user */
  storeUserLocale(user);

  const locale = getUserLocale(user);
  const translationMessages = yield call(
    getTranslationMessages,
    client,
    locale,
  );

  yield put({
    type: SagasType.SET_TRANSLATION_MESSAGE,
    payload: { locale, messages: translationMessages },
  });

  /* Initialize user permissions */
  const uiPermissions = yield call(getEmployeeUIPermissions, client);
  yield put({ type: 'SET_TENANT', payload: user.tid });
  yield put({ type: 'SET_USER', payload: user });

  yield call(callSetHeaderbarSubsidiary, client);
  yield call(callSetPrimarySubsidiary, client);

  yield put({ type: 'SET_USER_PERMISSIONS', payload: uiPermissions });
  yield put({ type: 'START_POUCH_LISTENER' });
  yield put({ type: SagasType.DISPLAY_USER_LOCALE, user });

  yield put({ type: 'INITIALIZE_APPLICATION' });

  /* Check local storage related to user */
  if (getLocalStorage('admin-user') === null) {
    /* create the admin-user */
    setLocalStorage('admin-user', JSON.stringify({ _id: payload.employee_id }));

    /* log if it's first time */
    yield put({ type: 'ACCESS_LOG_ONLINE_AUTH_SUCCESS' });
  }

  yield call(callFetchAllPreferenceSettings, client);

  /* All okay */
  yield put({ type: 'APP_READY' });
}

async function getEmployeeUIPermissions(app) {
  const permission = await apiCommon.queryDocs(app, 'permissions');

  return permission.data;
}

async function getTranslationMessages(app, locale) {
  const response = await apiCommon.queryDocs(app, 'translation', {
    lang: locale,
    client: 'admin',
  });

  return (response.data || {}).messages || {};
}

function* getEmployeeDetails(app, employeeId) {
  return yield call(apiEmployee.fetchEmployeeCompleteDetails, app, {
    id: employeeId,
  });
}

export function* initializeApp() {
  yield takeEvery('INITIALIZE_APPLICATION', callInitializeApp);
}

function* callInitializeApp() {
  yield put({ type: 'FETCH_TASKS' });
  yield put({ type: FETCH_INVENTORY_STATUS });
  yield put({ type: FETCH_TRANSACTION_STATUS });
  yield put({ type: FETCH_ENTITY_TYPE });
  yield put({ type: FETCH_EMPLOYEE_ROLE_AUTHORITY_LIST });
  yield put({ type: FETCH_ADDRESS_TYPE_LIST });
  yield put({ type: FETCH_COUNTRY_LIST });
}
